/**
 * Guideline 1.2 Report / Block (post-match card + leaderboard rows, web and mobile): reasons, labels and the
 * outcome text shown after the server answers. Platform-free; the UIs call `OnlineClient.reportPlayer/blockPlayer`.
 */
import type { PlayerActionResult, PlayerReportReason } from '../net/types';

export const REPORT_REASONS: readonly { id: PlayerReportReason; label: string }[] = [
  { id: 'offensive_name', label: 'Offensive name' },
  { id: 'cheating', label: 'Cheating' },
  { id: 'harassment', label: 'Harassment or abuse' },
  { id: 'other', label: 'Something else' },
];

export const BLOCK_CONFIRM = (name: string): string =>
  `Block ${name}? You won't be matched with them again and they're hidden from your leaderboard.`;

/** Toast text + tone for a player_action reply (or a failed request: null). */
export const moderationOutcome = (
  r: PlayerActionResult | null,
  name: string,
): { text: string; tone: 'success' | 'error' | 'info' } => {
  if (!r) return { text: "Couldn't reach the server. Try again.", tone: 'error' };
  if (r.ok) {
    if (r.action === 'report') return { text: `Thanks. Your report on ${name} was sent.`, tone: 'success' };
    if (r.action === 'block') return { text: `${name} is blocked.`, tone: 'success' };
    return { text: `${name} is unblocked.`, tone: 'info' };
  }
  switch (r.error) {
    case 'sign_in_required':
      return { text: 'Sign in to report or block players.', tone: 'error' };
    case 'rate_limited':
      return { text: 'Too many reports. Try again later.', tone: 'error' };
    case 'self':
      return { text: "That's you!", tone: 'info' };
    case 'unknown_player':
      return { text: "That player can't be found any more.", tone: 'error' };
    default:
      return { text: "Couldn't do that right now. Try again.", tone: 'error' };
  }
};

/** Run report / block on an online client that may lack the methods (older fakes): null = could not send. */
export const runModeration = async (
  online: {
    reportPlayer?: (id: string, reason: PlayerReportReason, matchId?: string) => Promise<PlayerActionResult>;
    blockPlayer?: (id: string, unblock?: boolean) => Promise<PlayerActionResult>;
  } | null | undefined,
  op: { kind: 'report'; playerId: string; reason: PlayerReportReason; matchId?: string } | { kind: 'block'; playerId: string },
): Promise<PlayerActionResult | null> => {
  try {
    if (op.kind === 'report') return (await online?.reportPlayer?.(op.playerId, op.reason, op.matchId)) ?? null;
    return (await online?.blockPlayer?.(op.playerId)) ?? null;
  } catch {
    return null;
  }
};
